import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ImageToSave } from '../images/models/image-to-save.model';
import { UntypedFormGroup } from '@angular/forms';
import { ImageTypes } from '../enums/image-types';
import { Question } from '../user-input/models/question.model';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { catchError, concatMap, EMPTY, interval, Subject, Subscription, takeUntil, takeWhile, tap } from 'rxjs';
import { BaseComponentDirective } from '../base/base.component';
import { ImageService } from '../services/image.service';
import { PathFinderService } from '../services/path-finder.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthorizationRequestService } from '../services/authorization-request.service';
import { NotificationService } from '../services/notification.service';
import { ActivatedRoute } from '@angular/router';
import { LoggingService } from '../services/logging.service';
import { ImageUploadStatus } from '../enums/image-upload-status.enum';
import { AuthorizationTypes } from '../authorization/enums/authorization-types';
import { OfflineModalComponent } from '../offline-modal/offline-modal.component';
import { Condition } from '../user-input/models/condition.model';

@Component({
  selector: 'app-image-library',
  templateUrl: './image-library.component.html',
  styleUrl: './image-library.component.scss'
})
export class ImageLibraryComponent extends BaseComponentDirective implements OnInit, OnDestroy {
  networkSub: Subscription;
  isOnline: boolean;
  constructor(
    public imageService: ImageService,
    private pathFinder: PathFinderService,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private authorizationRequestService: AuthorizationRequestService,
    private notifications: NotificationService,
    private route: ActivatedRoute,
    private loggingService: LoggingService) {
    super();
  }

  @Input() group: UntypedFormGroup;
  @Input() quoteStateId: number;
  @Input() save: Subject<number>;
  @Input() back: Subject<number>;
  @Input() displayImages: boolean;
  @Input() requiredImageType: number;
  @Input() questionForm: UntypedFormGroup;
  @Input()
  set imageData(imgs: Array<ImageToSave>) {
    this.resetImages();
    if (imgs) {
      imgs.forEach((img) => {
        const image = new ImageToSave();
        const paramName = img.parameterName ? img.parameterName : img.documentName;
        image.documentScanId = img.documentScanId;
        image.imageNumber = img.imageNumber ? img.imageNumber : 0;
        image.documentName = img.documentName;
        image.parameterName = img.parameterName;
        image.stampCode = img.stampCode;
        image.fileName = img.fileName;
        image.parameterName = paramName;
        image.tag = img.tag;
        image.threshold = img.threshold;
        image.allowPdf = img.allowPdf;
        if (img.imgAsBase64String) {
          image.imgAsBase64String = img.imgAsBase64String;
          image.thumbnailUrl = img.thumbnailUrl;
        }
        this.addImage(image);
      });
    }
  }

  @Output() finalizedNotifier = new EventEmitter<UntypedFormGroup>();
  @Output() busyNotifier = new EventEmitter<boolean>();
  @ViewChild('cameraInput', { read: ElementRef, static: true }) cameraInput: ElementRef;
  @ViewChild('fileInput', { read: ElementRef, static: true }) fileInput: ElementRef;

  isFromPurchaseMenu: boolean;
  allowAddImage: boolean;
  imagesToSave: Array<ImageToSave> = new Array<ImageToSave>();
  selectedIndex: number;
  previewString: SafeUrl;
  previewTitle: string;
  imageType: number;
  addImageLoading: boolean;
  isAdditionalImage: boolean;
  isPostPurchase: boolean;
  hasMismatch: boolean;
  authCheck: UntypedFormGroup;
  imageTypes: typeof ImageTypes;
  purchaseJourneyImages: Question[];
  intervalId: NodeJS.Timeout;
  readonly Status = ImageUploadStatus;
  status: number;
  timer$: Subscription = new Subscription();
  uploadSubject = new Subject<ImageToSave>();

  ngOnInit(): void {
    this.imageType = ImageTypes.ScannedDocument;
    this.imageTypes = ImageTypes;

    this.allowAddImage = this.group
      ? this.group.controls.allowAddImage.value
      : true;

    this.route.queryParamMap
      .pipe(
        tap((params) => {
          const imageParam = params.get('imageType');
          const value = params.get('isFromPurchaseMenu');
          this.isFromPurchaseMenu = value ? value.toLocaleLowerCase() === 'true' : false;

          if (imageParam) {
            this.imageType = parseInt(imageParam, 10);
            this.isPostPurchase = true;
          } else {
            this.imageType = this.requiredImageType === ImageTypes.VehiclePhoto
              ? ImageTypes.VehiclePhoto
              : ImageTypes.ScannedDocument;
            this.isPostPurchase = false;
          }
        }),
        takeUntil(this.componentDestroyed)
      ).subscribe();
    this.getImages();

    this.authCheck = this.imageService.getFormControl('V5AuthCheck', this.questionForm);
    if (this.authCheck) {
      this.authCheck.controls.value.setValue('false');
    }

    this.purchaseJourneyImages = this.pathFinder.getPurchaseFlowQuestionWithDoc();

    this.uploadSubject.
      pipe(
        concatMap(async (item) => this.onFileSelected(item)))
      .subscribe();
  }

  ngOnDestroy() {
    this.timer$.unsubscribe();
    if (this.uploadSubject) {
      this.uploadSubject.unsubscribe();
    }
  }

  getImages() {
    if (this.group && this.quoteStateId) {
      if (this.imageType === ImageTypes.VehiclePhoto) {
        this.getVehicleImages();
      } else {
        this.getScannedDocuments();
      }
    }
  }

  getVehicleImages() {
    this.imageService.getExistingVehicleImages$(this.quoteStateId)
      .pipe(
        catchError((err) => {
          this.notifications.dangerToast(
            `An error occurred while trying to get existing images for purchaseId ${this.quoteStateId}.`,
            err
          );
          this.group.controls.value.setValue(null);
          this.finalizedNotifier.emit(this.group);
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed)
      )
      .subscribe((result) => {
        if (result) {
          result.forEach(element => {
            const index = this.imagesToSave.findIndex(x => x && element.description === x.documentName);
            const checkToAdd = this.imagesToSave[index];
            if (checkToAdd.documentScanId !== undefined) {
              const image = new ImageToSave();
              image.documentName = checkToAdd.parameterName;
              image.parameterName = checkToAdd.parameterName;
              image.stampCode = checkToAdd.stampCode;
              image.documentScanId = element.vehicleImageId;
              image.imgAsBase64String = element.imgAsBase64String.toString();
              image.thumbnailUrl = this.sanitizer.bypassSecurityTrustUrl(
                element.imgAsBase64String.toString()
              );
              image.isSaved = true;

              this.imagesToSave.push(image);
            }
            else if (index !== -1) {
              this.imagesToSave[index].documentScanId = element.vehicleImageId;
              this.imagesToSave[index].fileName = element.fileName;
              this.imagesToSave[index].imgAsBase64String = element.imgAsBase64String.toString();
              this.imagesToSave[index].thumbnailUrl = this.sanitizer.bypassSecurityTrustUrl(
                element.imgAsBase64String.toString()
              );
              this.imagesToSave[index].isSaved = true;
            }
          });
        }
        this.checkImageStatus();
        this.checkIfAllImagesAdded();
      });
  }

  getScannedDocuments() {
    this.imageService.getExistingImages$(this.quoteStateId)
      .pipe(
        catchError((err) => {
          this.notifications.dangerToast(
            `An error occurred while trying to get existing scanned documents for purchaseId ${this.quoteStateId}.`,
            err
          );
          this.group.controls.value.setValue(null);
          this.finalizedNotifier.emit(this.group);
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed)
      )
      .subscribe((imageData) => {
        if (this.displayImages && this.imageType === ImageTypes.ScannedDocument) {
          this.addImagesFromAnsweredQuestions(imageData.images, imageData.answers);
        } else {
          const imagesForRequiredDocTypes = imageData.images.filter(
            (r) => r.parameterName === null
          );
          let index = 0;
          imagesForRequiredDocTypes.forEach((img) => {
            const image = new ImageToSave();
            image.imageNumber = img.imageNumber;
            image.documentName = this.getPurchaseJourneyDocumentName(img.stampCode, img.parameterName);
            image.parameterName = img.parameterName;
            image.stampCode = img.stampCode;
            image.documentScanId = img.documentScanId;
            image.fileName = img.fileName;
            image.imgAsBase64String = img.imgAsBase64String;
            if (img.fileName.endsWith('.pdf')) {
              image.thumbnailUrl = '../assets/images/pdf.svg';
            } else {
              image.thumbnailUrl = this.sanitizer.bypassSecurityTrustUrl(img.imgAsBase64String);
            }
            image.tag = img.tag;
            image.threshold = img.threshold;
            image.recognitionResult = img.recognitionResult;
            image.isSaved = true;
            this.addImageWithBase64String(image, index);
            index++;
          });
        }
        if (this.displayImages && this.imageType === ImageTypes.ScannedDocument) {
          const dashboardIndex = this.imagesToSave.map((obj) => ({ documentName: obj.documentName, stampCode: obj.stampCode, parameterName: obj.parameterName }))
            .findIndex((x) => x.stampCode === 'DB');

          if (dashboardIndex === -1) {
            const img = new ImageToSave();
            img.documentName = 'Dashboard';
            img.stampCode = 'DB';
            img.parameterName = 'Dashboard';
            this.imagesToSave.push(img);
          }
          this.checkIfAllImagesAdded();
        }
      });
  }

  addImageWithBase64String(file, index: number) {
    if (index > -1) {
      this.imagesToSave[index] = file;
    }
  }

  private getPurchaseJourneyDocumentName(stampCode, parameterName) {
    const question = this.purchaseJourneyImages?.find(x => x.parameterName === parameterName);
    return question?.images.filter(x => x.stampCode === stampCode).map(m => m.documentName).join();
  }

  addImagesFromAnsweredQuestions(imgs: ImageToSave[], answers) {
    let index = 0;
    imgs.forEach(img => {
      if (img.stampCode !== 'Ins S' && img.stampCode !== 'INV') {
        const image = new ImageToSave();
        image.documentName = this.getPurchaseJourneyDocumentName(img.stampCode, img.parameterName) || img.parameterName;
        image.stampCode = img.stampCode;
        image.parameterName = img.parameterName;
        image.tag = img.tag;
        image.imageNumber = index;
        image.threshold = img.threshold;
        image.documentScanId = img.documentScanId;
        image.imgAsBase64String = img.imgAsBase64String;
        if (img.fileName.endsWith('.pdf')) {
          image.thumbnailUrl = '../assets/images/pdf.svg';
        } else {
          image.thumbnailUrl = this.sanitizer.bypassSecurityTrustUrl(img.imgAsBase64String);
        }
        image.recognitionResult = img.recognitionResult;
        image.fileName = img.fileName;
        image.extension = img.fileName.substring(image.fileName.lastIndexOf('.'));
        image.hasMismatch = img.hasMismatch;
        image.allowPdf = img.allowPdf ? img.allowPdf : false;
        image.isSaved = true;
        this.addImageWithBase64String(image, index);
        this.checkImageStatus();
        index += 1;
      }
    });
    const questions = this.pathFinder.getAnsweredImageQuestions(answers);
    questions.forEach((question) => {
      question.images.forEach((element) => {
        const existingIndex = this.imagesToSave
          .map((obj) => ({ documentName: obj.documentName, stampCode: obj.stampCode, parameterName: obj.parameterName }))
          .findIndex((x) => x.stampCode === element.stampCode || x.parameterName === question.parameterName && x.stampCode === 'Unknown');
        if (element.isVideo) {
          // Do nothing. Get rid of Video image
          return;
        }

        if (element?.condition && !this.checkImageCondition(element.condition)) {
          return;
        }
        if (existingIndex === -1) {
          const image = new ImageToSave();
          image.documentName = element.documentName
          image.stampCode = element.stampCode;
          image.parameterName = question.parameterName;
          image.tag = element.tag;
          image.threshold = element.threshold;
          image.hasMismatch = element.hasMismatch;
          image.recognitionResult = element.recognitionResult;
          image.allowPdf = element.allowPdf ? element.allowPdf : false;
          image.status = ImageUploadStatus.None;
          this.addImageWithBase64String(image, index);
          index += 1;
        } else {
          this.imagesToSave[existingIndex].documentName = element.documentName;
          this.imagesToSave[existingIndex].allowPdf = element.allowPdf ? element.allowPdf : false;
        }
      });
    });

    this.getAuthRequestsAndSetMismatch();

    this.checkIfAllImagesAdded();
  }

  checkImageCondition(condition: Condition) {
    return this.pathFinder.evaluateCondition(condition);
  }

  getAuthRequestsAndSetMismatch() {
    this.authorizationRequestService.getRequestByQuoteStateId$(this.quoteStateId).pipe(
      catchError((err) => {
        this.notifications.dangerToast(
          'An error occurred while trying to get auth request by quoteStateId.',
          err
        );
        return EMPTY;
      }),
      takeUntil(this.componentDestroyed)
    )
      .subscribe((requests) => {
        const check = this.imagesToSave.some(x => x.recognitionResult <= x.threshold && x.recognitionResult != null);
        const authRequests = requests;
        if (authRequests && authRequests.length > 0) {
          const request = requests.find(x => x.authorizationTypeId === AuthorizationTypes.V5QualityCheck);
          const uiCheck = request ? request?.approved == null : this.imagesToSave.some(x => x.status === this.Status.ImageRecognitionFailed);
          if (check && uiCheck) {
            this.setMismatch();
            this.imageService.checkAuthorization(this.authCheck, true);
          }
          else {
            this.setMismatchApproved();
          }
        } else {
          if (check) {
            this.setMismatch();
          }
        }
      });
  }

  setMismatchApproved() {
    this.imagesToSave.forEach(element => {
      if (element.hasMismatch) {
        element.hasMismatch = false;
        element.status = ImageUploadStatus.Completed;
      }
    });
  }

  setMismatch() {
    this.imagesToSave.forEach(image => {
      if (image.recognitionResult != null && image.recognitionResult < image.threshold) {
        image.hasMismatch = true;
      }
    });
  }

  addImage(image) {
    this.imagesToSave.push(image);
  }

  checkImageStatus() {
    this.imagesToSave.forEach(img => {
      if (img.thumbnailUrl) {
        img.status = ImageUploadStatus.Completed;
      } else {
        img.status = ImageUploadStatus.None;
      }

      if (this.requiredImageType == ImageTypes.ScannedDocument) {
        if (img.hasMismatch && !this.isPostPurchase) {
          img.status = ImageUploadStatus.ImageRecognitionFailed;
        }
      }
    })
  }

  checkIfAllImagesAdded() {
    if (this.group) {
      if (this.imageService.checkAllImagesAdded(this.imagesToSave, this.imageType, this.isPostPurchase)) {
        this.group.controls.value.setValue('0');
      } else {
        this.group.controls.value.setValue(null);
      }
      this.finalizedNotifier.emit(this.group);
      if (this.displayImages) {
        this.imageService.allImagesAdded.emit(this.imageService.checkAllImagesAdded(this.imagesToSave, this.imageType, this.isPostPurchase));
      }
    }
  }

  get isHidden(): boolean {
    if (!this.group) {
      return false;
    }
    return this.group.controls.isHidden.value;
  }

  deleteImage(image) {
    image.image = null;
    image.imgAsBase64String = null;
    image.thumbnailUrl = null;
    image.hasMismatch = false;
    image.status = ImageUploadStatus.None;
    if (image.documentScanId) {
      this.imageService.deleteImage$(image.documentScanId, this.requiredImageType).pipe(
        tap(() => {
          if (this.canBeDeleted(image)) {
            this.removeImage(image);
          }
          this.checkIfAllImagesAdded();
        })
      ).subscribe();
    }
  }

  downloadPdf(file) {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', file.imgAsBase64String);
    link.setAttribute('download', file.fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  showPreview(photo, content) {
    if (photo.fileName.endsWith('.pdf')) {
      if (this.isPostPurchase) {
        this.downloadPdf(photo);
      }
      return;
    }
    if (photo.documentScanId) {
      if (this.imageType === ImageTypes.VehiclePhoto) {
        this.imageService
          .getImagePreview$(photo.documentScanId, ImageTypes.VehiclePhoto)
          .pipe(
            catchError((err) => {
              this.notifications.dangerToast(
                `An error occurred while trying to get existing images for documentScanId ${photo.documentScanId}.`,
                err
              );
              return EMPTY;
            }),
            takeUntil(this.componentDestroyed)
          )
          .subscribe((result) => {
            this.previewString =
              this.sanitizer.bypassSecurityTrustUrl(
                result.imgAsBase64String
              );
            this.previewTitle = result.description;
            this.modalService.open(content, { size: 'lg' });
          });
      } else if (this.imageType === ImageTypes.ScannedDocument) {
        this.imageService
          .getImagePreview$(photo.documentScanId, ImageTypes.ScannedDocument)
          .pipe(
            catchError((err) => {
              this.notifications.dangerToast(
                `An error occurred while trying to get existing images for documentScanId ${photo.documentScanId}.`,
                err
              );
              return EMPTY;
            }),
            takeUntil(this.componentDestroyed)
          )
          .subscribe((result) => {
            this.previewString = this.sanitizer.bypassSecurityTrustUrl(
              result.imgAsBase64String
            );
            this.previewTitle = this.getPurchaseJourneyDocumentName(photo.stampCode, photo.parameterName) || photo.parameterName;
            this.modalService.open(content, { size: 'lg' });
          });
      }
    } else {
      this.previewString = photo.thumbnailUrl;
      this.previewTitle = this.getPurchaseJourneyDocumentName(photo.stampCode, photo.parameterName) || photo.parameterName;
      this.modalService.open(content, { size: 'lg' });
    }
  }

  get showUploadButton() {
    return this.requiredImageType === ImageTypes.ScannedDocument;
  }

  onAddImageClick(): void {
    if (this.displayImages) {
      this.imageService.showModal(this.imagesToSave, this.requiredImageType, this.isPostPurchase);
    } else {
      this.isAdditionalImage = true;
      const image = new ImageToSave();
      image.imageNumber = this.imagesToSave.length + 1;
      image.documentName = this.getPurchaseJourneyDocumentName(this.imagesToSave[0].stampCode, this.imagesToSave[0].parameterName);
      image.stampCode = this.imagesToSave[0].stampCode;
      image.parameterName = this.imagesToSave[0].parameterName;
      image.allowPdf = this.imagesToSave[0].allowPdf;

      const index = this.imagesToSave.length;
      this.openCamera(image, index);
    }
  }

  openCamera(image, i) {
    if (image.imgAsBase64String != null) {
      this.deleteImage(image);
    }
    this.imagesToSave[i].documentName = this.getPurchaseJourneyDocumentName(image.stampCode, image.parameterName) || image.parameterName;
    this.imagesToSave[i].parameterName = image.parameterName;
    this.imagesToSave[i].stampCode = image.stampCode;
    this.imagesToSave[i].tag = image.tag;
    this.imagesToSave[i].threshold = image.threshold;
    this.imagesToSave[i].imageNumber = i;
    this.cameraInput.nativeElement.imageIndex = i;
    this.cameraInput.nativeElement.accept = this.getAcceptType(image.allowPdf);
    this.cameraInput.nativeElement.click(i);
  }

  async openFile(image, i) {
    this.imagesToSave[i].documentName = this.getPurchaseJourneyDocumentName(image.stampCode, image.parameterName) || image.parameterName;
    this.imagesToSave[i].parameterName = image.parameterName;
    this.imagesToSave[i].stampCode = image.stampCode;
    this.imagesToSave[i].tag = image.tag;
    this.imagesToSave[i].threshold = image.threshold;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const windRef = window as any;
    try {
      let options = this.imageService.pickerOpts;
      if (image.allowPdf) {
        options = this.imageService.incPdfPickerOpts;
      }
      const [fileHandle] = await windRef.showOpenFilePicker(options);
      image = await fileHandle.getFile();
      this.addQueuedImage(image, i);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
    }
  }

  async addQueuedImage(event, imageIndex) {
    let image;
    let index;
    if (event instanceof Event) {
      image = (event.target as HTMLInputElement).files?.[0];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      index = (event.currentTarget as any).imageIndex;
      if (!image) {
        return;
      }
    } else {
      image = event;
      index = imageIndex;
    }

    const queuedImage = this.imagesToSave[index];
    const isAlreadyUploading = queuedImage?.status === ImageUploadStatus.Queued
      || queuedImage?.status === ImageUploadStatus.Loading;

    if (isAlreadyUploading) {
      return;
    }

    this.imagesToSave[index].image = image;
    this.imagesToSave[index].imageNumber = index;
    this.imagesToSave[index].status = ImageUploadStatus.Queued;
    this.imagesToSave[index].progress = 0;
    this.uploadSubject.next(this.imagesToSave[index]);
  }

  async onFileSelected(imgToSave) {
    imgToSave.status = ImageUploadStatus.Loading;
    this.timer$ = interval(25).pipe(
      takeWhile(val => val < 100)
    ).subscribe(() => {
      imgToSave.progress++;
    });

    if (!imgToSave.image || typeof imgToSave.image.size !== 'number') {
      const message = `FileSelected method  ${imgToSave?.documentName} image for quoteStateId ${this.quoteStateId} - Image is : ${(imgToSave.image instanceof File) ? 'valid' : undefined}`;
      this.loggingService.logInfo(message);
    }
    const imageExtension = '.' + imgToSave.image.type.substring(imgToSave.image.type.indexOf('/') + 1, imgToSave.image.type.length);
    const isPdf = imageExtension === '.pdf';
    let fileTypeAllowed = imageExtension === '.png' || imageExtension === '.jpg' || imageExtension === '.jpeg';
    if (this.imagesToSave[imgToSave.imageNumber].allowPdf && isPdf) {
      fileTypeAllowed = true;
    }
    if (!fileTypeAllowed) {
      this.notifications.warningToast(
        `File ${imgToSave.image.name} with type ${imageExtension} to upload must be an image`
      );
    }
    if (fileTypeAllowed && imgToSave.image.size > 0) {
      let blob;
      const imageQualitySetting = this.pathFinder.getPurchaseSettingAsFloat('ImageUploadQuality');
      const imageUploadScalingFileSize = this.pathFinder.getPurchaseSettingAsFloat('ImageUploadScalingFileSize');
      if (!isPdf && imgToSave.image.size > imageUploadScalingFileSize) {
        blob = await this.imageService.reduceFileSize(imgToSave.image, imageQualitySetting, this.loggingService, this.quoteStateId, imgToSave);
      } else {
        blob = blob && blob?.size > 0 ? blob : imgToSave.image;
      }
      if (!blob?.name) {
        blob.name = imgToSave.image.name;
      }
      if (blob && blob?.size && this.allowAddImage) {
        imgToSave.image = blob;
        const blobUrl = URL.createObjectURL(blob);
        if (await this.imageService.checkBlobUrl(blobUrl) && this.imageService.isBlobURL(blobUrl)) {
          if (isPdf) {
            imgToSave.thumbnailUrl = '../assets/images/pdf.svg';
          } else {
            imgToSave.thumbnailUrl = this.imageService.createUrl(blob);
          }
          try {
            const result = await this.imageService.readFileAsync(blob);
            imgToSave.imgAsBase64String = result?.toString();
            imgToSave.imageNumber = imgToSave.imageNumber;

            imgToSave.extension = imageExtension;
            imgToSave.fileName = this.imageService.getFileName(imgToSave);

            if (imgToSave.tag && imgToSave.imgAsBase64String && !this.isPostPurchase) {
              this.imageService.getImageRecognition$(imgToSave, this.quoteStateId).pipe(
                tap((res) => {
                  if (this.imageService.checkAllImagesAdded(this.imagesToSave, this.imageType, this.isPostPurchase)) {
                    // Create unique id for auth value parameter
                    const u = Date.now().toString(16) + Math.random().toString(16) + '0'.repeat(16);
                    const guid = [u.substring(0, 8), u.substring(8, 4), '4000-8' + u.substring(13, 3), u.substring(16, 12)].join('-');
                    const v5Picture = this.imageService.getFormControl('V5Picture', this.questionForm);
                    if (v5Picture) {
                      v5Picture.value.value = guid;
                    }
                    this.group.controls.value.setValue(guid);
                    this.finalizedNotifier.emit(this.group);
                  }
                  imgToSave.recognitionResult = res.probability;
                  imgToSave.hasMismatch = res.hasMismatch;
                  this.checkForMismatch();
                  this.saveImageAsync(imgToSave);
                }),
                catchError(err => {
                  const error = {
                    message: `GetImageRecognition ERROR result for quoteStateId ${this.quoteStateId} - res: ${JSON.stringify(err)}`,
                  };
                  this.loggingService.logError(error);
                  this.notifications.dangerToast('Failed to get image recognition', err);
                  this.timer$.unsubscribe();
                  this.imagesToSave[imgToSave.imageNumber].status = ImageUploadStatus.ImageRecognitionFailed;
                  this.saveImageAsync(imgToSave);
                  return EMPTY;
                }),
                takeUntil(this.componentDestroyed)
              ).subscribe();
            } else {
              this.saveImageAsync(imgToSave);
            }
          } catch (error) {
            this.loggingService.logError(error);
          }
        } else {
          imgToSave.thumbnailUrl = null;
          this.imagesToSave[imgToSave.imageNumber].status = ImageUploadStatus.None;
        }
      }
      else {
        this.imagesToSave[imgToSave.imageNumber].status = ImageUploadStatus.None;
      }
    } else {
      this.imagesToSave[imgToSave.imageNumber].status = ImageUploadStatus.None;
    }
  }

  saveImageAsync(imgToSave: ImageToSave) {
    return this.imageService.saveImage$(this.quoteStateId, this.requiredImageType, this.isPostPurchase, imgToSave).pipe(
      tap((response) => {
        if (response.success) {
          if (imgToSave.hasMismatch) {
            imgToSave.status = ImageUploadStatus.ImageRecognitionFailed;
            this.hasMismatch = false;
          }
          else {
            imgToSave.status = ImageUploadStatus.Completed;
          }
        } else {
          imgToSave.status = ImageUploadStatus.Failed;
        }
        imgToSave.documentScanId = response.id;
        this.checkIfAllImagesAdded();
      }),
      catchError(err => {
        imgToSave.status = ImageUploadStatus.Failed;
        this.timer$.unsubscribe();
        const error = {
          message: `ERROR Saving image for quoteStateId ${this.quoteStateId} - res: ${JSON.stringify(err)}`,
        };
        this.loggingService.logError(error);
        this.notifications.dangerToast('Failed to save image', err);
        return EMPTY;
      }),
      takeUntil(this.componentDestroyed)
    ).subscribe();
  }

  removeImage(imageToDelete: ImageToSave) {
    const index = this.imagesToSave.indexOf(imageToDelete);
    this.imagesToSave.splice(index, 1);
  }

  canBeDeleted(photo: ImageToSave) {
    if (this.displayImages || this.allowAddImage) {
      return this.imagesToSave.filter(item => item.documentName === photo.documentName).length > 1;
    } else {
      return false;
    }
  }

  loadingImage(image: ImageToSave): string {
    if (image.extension === '.pdf') {
      return '';
    }
    return this.loadingBar(image.status);
  }

  loadingBar(status: ImageUploadStatus): string {
    switch (status) {
      case ImageUploadStatus.Completed:
        return 'bar-green completed';
      case ImageUploadStatus.Failed:
        return 'bar-red failed';
      case ImageUploadStatus.Loading:
        return 'loading';
      case ImageUploadStatus.Queued:
        return 'bar-queued';
      case ImageUploadStatus.ImageRecognitionFailed:
        return 'bar-red failed';
      default:
        return '';
    }
  }

  isFailed(status) {
    return status === ImageUploadStatus.Failed || status === ImageUploadStatus.ImageRecognitionFailed;
  }

  apiResult(status) {
    switch (status) {
      case ImageUploadStatus.Completed:
      case ImageUploadStatus.Failed:
      case ImageUploadStatus.ImageRecognitionFailed:
        return true;
      default:
        return false;
    }
  }

  checkStatus(status) {
    switch (status) {
      case ImageUploadStatus.Completed:
        return 'loading-completed';
      case ImageUploadStatus.Failed:
        return 'loading-failed';
      case ImageUploadStatus.Loading:
        return 'loading';
      case ImageUploadStatus.Queued:
        return 'loading-queued';
      case ImageUploadStatus.ImageRecognitionFailed:
        return 'loading-failed';
      default:
        return '';
    }
  }

  get checkUploadFailed() {
    return this.imagesToSave.some(some => some.status === ImageUploadStatus.Failed)
  }

  get imageRecognitionFailed() {
    return this.imagesToSave.some(some => some.status === ImageUploadStatus.ImageRecognitionFailed)
  }

  get checkAddImageButton() {
    if (this.imagesToSave.length === 0) {
      return false;
    }
    return this.imagesToSave.length % 5 === 0;
  }

  resetImages() {
    this.imagesToSave = new Array<ImageToSave>();
  }

  checkForMismatch() {
    let mismatch = false;
    this.imagesToSave.forEach(element => {
      if (element.hasMismatch) {
        mismatch = true;
      }
    });
    if (mismatch) {
      this.hasMismatch = true;
    } else {
      this.hasMismatch = false;
    }
    this.checkAuthorization();
  }

  checkAuthorization() {
    if (this.authCheck) {
      if (this.hasMismatch) {
        this.authCheck.controls.value.setValue('true');
      } else {
        this.authCheck.controls.value.setValue('false');
      }
    }
  }

  showOfflineModal() {
    this.modalService.open(OfflineModalComponent, {
      keyboard: false,
      backdrop: 'static',
      centered: true,
      size: 'lg',
    });
  }

  getAcceptType(allowPdf: boolean) {
    if (allowPdf) {
      return 'image/*,.pdf';
    } else {
      return 'image/*';
    }
  }
}
